import { useMutation } from "@tanstack/react-query";
import { Text } from "@ui-kitten/components";
import Button from "components/Button";
import LoginOrSignupForm from "components/LoginOrSignupForm";
import TextInput from "components/TextInput";
import { setEmail } from "features/slices/loginFlowSlice";
import { Field, Formik } from "formik";
import { useAppDispatch } from "hooks/useAppDispatch";
import i18n from "plugins/localization";
import { createContactFetcher } from "queries/user.queries";
import React, { useState } from "react";
import { View } from "react-native";
import userServices from "services/user.services";
import * as yup from "yup";

interface StepOneProps {
  setStep: React.Dispatch<React.SetStateAction<number>>;
}

const emailSchema = yup.object().shape({
  email: yup
    .string()
    .email(i18n.t("errors.form.email"))
    .required(i18n.t("errors.form.required")),
});

const StepOne: React.FC<StepOneProps> = ({ setStep }) => {
  const [showLoginForm, setShowLoginForm] = useState(false);
  const { mutateAsync: createContact } = useMutation(createContactFetcher);
  const dispatch = useAppDispatch();

  const addEmailToContact = (email: string) => {
    createContact({ source: "GROUP_INVITE", email });
  };

  const onSubmit = async ({ email }: { email: string }) => {
    const emailExists = await userServices.userExists(email);
    if (emailExists) {
      dispatch(setEmail(email));
      setShowLoginForm(true);
    } else {
      addEmailToContact(email);
      setStep(2);
    }
  };

  return (
    <View>
      <Text
        category="h3"
        style={{ textAlign: "center", fontWeight: "700", marginVertical: 20 }}
      >
        {i18n.t("screens.GroupReservation.joinTheCrowd")}
      </Text>
      {showLoginForm ? (
        <LoginOrSignupForm
          loginTitle="Sign In"
          signupTitle="Sign Up"
          initialForm="login"
        />
      ) : (
        <View>
          <Text category="h6" style={{ marginVertical: 10 }}>
            {i18n.t("screens.GroupReservation.enterYourEmail")}
          </Text>
          <Formik
            initialValues={{ email: "" }}
            onSubmit={onSubmit}
            validationSchema={emailSchema}
          >
            {({ isValid, handleSubmit }) => (
              <View>
                <Field
                  placeholder={i18n.t("labels.email")}
                  component={TextInput}
                  name="email"
                  appearance="default"
                ></Field>
                <Text category="c2" style={{ marginTop: 15 }}>
                  {i18n.t("screens.GroupReservation.consent")}
                </Text>
                <Button
                  style={{ marginVertical: 15 }}
                  disabled={!isValid}
                  onPress={() => handleSubmit()}
                >
                  {i18n.t("common.continue")}
                </Button>
              </View>
            )}
          </Formik>
        </View>
      )}
      {showLoginForm ? (
        <View style={{ alignItems: "center" }}>
          <Button appearance="ghost" onPress={() => setShowLoginForm(false)}>
            {i18n.t("common.back")}
          </Button>
        </View>
      ) : (
        <Text style={{ textAlign: "center" }}>
          {i18n.t("common.alreadyAMember") as string}{" "}
          <Button appearance="ghost" onPress={() => setShowLoginForm(true)}>
            {i18n.t("common.clickHere")}
          </Button>
        </Text>
      )}
    </View>
  );
};

export default StepOne;
